import React, { useState, useEffect, useContext, useRef } from 'react'
import ModalContext from '../../../contexts/ModalContext'
import { ServicesContext } from '../../../contexts/ServicesContext'
import { FeedbackContext } from '../../../contexts/FeedbackContext'
import UserSelect from './Select/UserSelect' 
import RNCSuccessModal from './RNCSuccessModal'
import axios from '../../../config/axios'
import angleDown from '../../../assets/production-flow/rnc/angledown.svg'
import crossLight from '../../../assets/stock/FiltersHeader/cross.svg'
import closeBig from '../../../assets/production-flow/rnc/closebig.svg'
import lightSearch from '../../../assets/production-flow/rnc/lightsearch.svg'
import switchCheck from '../../../assets/production-flow/rnc/switchcheck.svg'
import plus from '../../../assets/production-flow/rnc/plus.svg'
import trash from '../../../assets/production-flow/rnc/trash.svg'
import trashLight from '../../../assets/production-flow/rnc/trashlight.svg'
import whitePlus from '../../../assets/production-flow/whiteplus.svg'

import {
  Container,
  HeaderContainer,
  HeaderTitle,
  CloseButton,
  ContentContainer,
  FormGrid,
  FormGroup,
  Label,
  Input,
  SearchInputWrapper,
  SearchIcon,
  SelectWrapper,
  SelectIcon,
  Select,
  Section,
  CausaPertinenteWrapper,
  CausaWrapper,
  PertinenteWrapper,
  ToggleSwitchLabel,
  ToggleSwitchInput,
  ToggleSwitchSlider,
  ToggleSwitchThumb,
  ActionItemsSection,
  ActionItemsHeader,
  ActionItemRow,
  ResponsibleInputWrapper,
  IconButton,
  AddButton,
  ButtonContainer,
  CancelButton,
  SaveButton,
} from './style'

interface ProvidenceItem {
  id: number
  description: string
  deadline: string
  responsibleId: number
  responsibleName: string
}

interface User {
  id: number
  name: string
}

const RNCModal: React.FC = () => {
  const { setModalActive } = useContext(ModalContext)
  const { service } = useContext(ServicesContext)
  const { setFeedback } = useContext(FeedbackContext)

  const [operationNumber, setOperationNumber] = useState('')
  const [allocation, setAllocation] = useState('Interno')
  const [quantity, setQuantity] = useState('')
  const [nonConformQuantity, setNonConformQuantity] = useState('')
  const [description, setDescription] = useState('')
  const [cause, setCause] = useState('')
  const [relevant, setRelevant] = useState(true)
  const [correctiveAction, setCorrectiveAction] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)

  const [providences, setProvidences] = useState<ProvidenceItem[]>([
    {
      id: Date.now(),
      description: '',
      deadline: '',
      responsibleId: 1,
      responsibleName: '',
    },
  ])

  useEffect(() => {
    if (service) {
      const orderItem = service.orderItem || service.OrderItem
      const order = orderItem?.order || orderItem?.Order

      setOperationNumber(order?.saleNumberPloomes || '')
      setDescription(orderItem?.ploomesProductName || '')

      const stockInProduction = service.stockInProduction || []
      const materialsOnly = stockInProduction.filter(
        (item: any) => item.stockId !== null
      )
      const materialCount = materialsOnly.length

      setQuantity(materialCount.toString())
    }
  }, [service, service?.stockInProduction?.length])

  const handleClose = () => {
    setModalActive(false)
  }

  const handleAddProvidence = () => {
    setProvidences([
      ...providences,
      {
        id: Date.now(),
        description: '',
        deadline: '',
        responsibleId: 1,
        responsibleName: '',

      },
    ])
  }

  const handleDeleteProvidence = (id: number) => {
    if (providences.length > 1) {
      setProvidences(providences.filter((item) => item.id !== id))
    }
  }

  const isDeleteDisabled = providences.length === 1

  const handleProvidenceChange = (
    id: number,
    field: keyof ProvidenceItem,
    value: string | number,
  ) => {
    setProvidences(
      providences.map((p) => (p.id === id ? { ...p, [field]: value } : p)),
    )
  }

  const handleResponsibleChange = (index: number, user: User | null) => {
    setProvidences(
      providences.map((p, i) =>
        i === index
          ? { 
              ...p, 
              responsibleId: user?.id || 1, 
              responsibleName: user?.name || '' 
            }
          : p,
      ),
    )
  }



  const areProvidencesValid = () => {
    return providences.every(
      (p) =>
        p.description.trim() !== '' &&
        p.deadline.trim() !== '' &&
        p.responsibleId &&
        p.responsibleName.trim() !== '',
    )
  }

  const areMainFieldsValid = () => {
    return (
      operationNumber &&
      allocation &&
      nonConformQuantity &&
      description &&
      cause &&
      correctiveAction
    )
  }

  const handleSave = async () => {
    if (isSaving) return

    if (!areMainFieldsValid()) {
      setFeedback?.({
        isOpen: true,
        statusOk: false,
        message: 'Por favor, preencha todos os campos obrigatórios',
      })
      return
    }

    if (!areProvidencesValid()) {
      setFeedback?.({
        isOpen: true,
        statusOk: false,
        message:
          'Por favor, preencha todos os campos das providências (responsável, descrição e prazo)',
      })
      return
    }

    if (!service?.id) {
      setFeedback?.({
        isOpen: true,
        statusOk: false,
        message: 'Serviço não encontrado',
      })
      return
    }

    setIsSaving(true)

    const rncData = {
      operationNumber: operationNumber.toString(),
      allocation,
      quantity: parseInt(nonConformQuantity) || 0,
      description,
      relevant,
      cause,
      correction: correctiveAction,
      serviceId: service.id,
      providences: providences.map((p) => ({
        description: p.description,
        deadline: p.deadline,
        responsibleId: p.responsibleId,
      })),
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/rnc`,
        rncData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        },
      )

      setShowSuccessModal(true)
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.message || error.message
        : 'Erro desconhecido'

      setFeedback?.({
        isOpen: true,
        statusOk: false,
        message: `Erro ao gerar RNC: ${errorMessage}`,
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleSuccessClose = () => {
    setShowSuccessModal(false)
    setModalActive(false)
  }

  const handleGoToReports = () => {
    setShowSuccessModal(false)
    setModalActive(false)
  }

  const rncNumber = service?.codeOfOP || 'OP-000000'
  const orderItem = service?.orderItem || service?.OrderItem
  const order = orderItem?.order || orderItem?.Order
  const clientName =
    order?.client?.companyName ||
    order?.Client?.companyName ||
    'Cliente não informado'

  return (
    <>
      {!showSuccessModal && (
        <Container>
          <HeaderContainer>
            <HeaderTitle>
              Registro de não conformidade (RNC): {rncNumber}
            </HeaderTitle>
            <CloseButton onClick={handleClose}>
              <img src={closeBig} alt='Fechar' width={12} height={12} />
            </CloseButton>
          </HeaderContainer>

          <ContentContainer>
            <FormGrid cols={2}>
              <FormGroup>
                <Label>Número da OP</Label>
                <Input type='text' value={rncNumber} disabled />
              </FormGroup>

              <FormGroup>
                <Label>Número da operação</Label>
                <Input
                  type='text'
                  value={operationNumber}
                  onChange={(e) => setOperationNumber(e.target.value)}
                  placeholder='Digite aqui'
                />
              </FormGroup>

              <FormGroup>
                <Label>Cliente</Label>
                <SearchInputWrapper>
                  <Input type='text' value={clientName} disabled />
                  <SearchIcon>
                    <img src={lightSearch} alt='Buscar' width={18} height={18} />
                  </SearchIcon>
                </SearchInputWrapper>
              </FormGroup>

              <FormGroup>
                <Label>Alocação</Label>
                <SelectWrapper>
                  <Select
                    value={allocation}
                    onChange={(e) => setAllocation(e.target.value)}
                  >
                    <option value='Interno'>Interno</option>
                    <option value='Externo'>Externo</option>
                  </Select>
                  <SelectIcon>
                    <img src={angleDown} alt='Seta' width={18} height={18} />
                  </SelectIcon>
                </SelectWrapper>
              </FormGroup>
            </FormGrid>

            <FormGroup>
              <Label>Descrição do produto</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled
              />
            </FormGroup>

            <FormGrid cols={2} style={{ paddingTop: '16px' }}>
              <FormGroup>
                <Label>Quantidade total de peças</Label>
                <Input
                  type='number'
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  disabled
                />
              </FormGroup>

              <FormGroup>
                <Label>Quantidade não conforme</Label>
                <Input
                  type='number'
                  value={nonConformQuantity}
                  onChange={(e) => setNonConformQuantity(e.target.value)}
                  placeholder='Digite aqui'
                />
              </FormGroup>
            </FormGrid>

            <ActionItemsSection
              style={{
                borderTop: '1px solid #ebebeb',
                borderBottom: '1px solid #ebebeb',
                paddingTop: '16px',
                paddingBottom: '16px',
              }}
            >
              <ActionItemsHeader>
                <Label>Responsável</Label>
                <Label>Descrição</Label>
                <Label>Prazo</Label>
                <div></div>
              </ActionItemsHeader>

              {providences.map((item, index) => (
                <ActionItemRow key={item.id}>
                  <ResponsibleInputWrapper>
                    <UserSelect<User>
                      apiUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/user`}
                      displayField="name"
                      value={item.responsibleName ? {
                        id: item.responsibleId,
                        name: item.responsibleName
                      } : null}
                      onChange={(user) => handleResponsibleChange(index, user)}
                    />
                  </ResponsibleInputWrapper>
                  <Input
                    type='text'
                    value={item.description}
                    placeholder='Digite a descrição'
                    onChange={(e) =>
                      handleProvidenceChange(
                        item.id,
                        'description',
                        e.target.value,
                      )
                    }
                  />
                  <Input
                    type='date'
                    value={item.deadline}
                    onChange={(e) =>
                      handleProvidenceChange(item.id, 'deadline', e.target.value)
                    }
                  />
                  <IconButton
                    type='button'
                    onClick={() => handleDeleteProvidence(item.id)}
                    disabled={isDeleteDisabled}
                  >
                    <img
                      src={isDeleteDisabled ? trashLight : trash}
                      alt='Deletar'
                      width={20}
                      height={20}
                    />
                  </IconButton>
                </ActionItemRow>
              ))}

              <AddButton type='button' onClick={handleAddProvidence}>
                <img src={plus} alt='Adicionar' width={12} height={12} />
                Adicionar
              </AddButton>
            </ActionItemsSection>

            <Section style={{ paddingTop: '16px' }}>
              <CausaPertinenteWrapper>
                <CausaWrapper>
                  <Label>Causa</Label>
                  <Input
                    type='text'
                    value={cause}
                    onChange={(e) => setCause(e.target.value)}
                    placeholder='Digite aqui'
                  />
                </CausaWrapper>
                <PertinenteWrapper>
                  <Label>Pertinente</Label>
                  <ToggleSwitchLabel htmlFor='relevant-toggle'>
                    <ToggleSwitchInput
                      type='checkbox'
                      id='relevant-toggle'
                      checked={relevant}
                      onChange={(e) => setRelevant(e.target.checked)}
                    />
                    <ToggleSwitchSlider checked={relevant}>
                      <ToggleSwitchThumb checked={relevant}>
                        {relevant ? (
                          <img
                            src={switchCheck}
                            alt='Check'
                            style={{
                              width: '12px',
                              height: '12px',
                            }}
                          />
                        ) : (
                          <img
                            src={crossLight}
                            alt='Close'
                            style={{
                              width: '12px',
                              height: '12px',
                              filter: 'brightness(0) invert(1)'
                            }}
                          />
                        )}
                      </ToggleSwitchThumb>
                    </ToggleSwitchSlider>
                  </ToggleSwitchLabel>
                </PertinenteWrapper>
              </CausaPertinenteWrapper>
            </Section>

            <Section>
              <FormGrid cols={2}>
                <FormGroup>
                  <Label>Ação corretiva efetiva</Label>
                  <Input
                    value={correctiveAction}
                    onChange={(e) => setCorrectiveAction(e.target.value)}
                    placeholder='Digite aqui'
                  />
                </FormGroup>
              </FormGrid>
            </Section>
          </ContentContainer>

          <ButtonContainer>
            <CancelButton type='button' onClick={handleClose} disabled={isSaving}>
              Cancelar
            </CancelButton>
            <SaveButton type='button' onClick={handleSave} disabled={isSaving}>
              <img src={whitePlus} alt='Gerar' width={12} height={12} />
              {isSaving ? 'Gerando...' : 'Gerar RNC'}
            </SaveButton>
          </ButtonContainer>
        </Container>
      )}

      <RNCSuccessModal
        isOpen={showSuccessModal}
        onClose={handleSuccessClose}
        onGoToReports={handleGoToReports}
      />
    </>
  )
}

export default RNCModal